import type { Unit } from "@sins2-atlas/game-data";
import { getRouteApi, Link } from "@tanstack/react-router";
import { clsx } from "clsx";
import { type ReactNode, useState } from "react";
import { z } from "zod";
import { ArtImage } from "../components/art-image";
import { ExoticCounts } from "../components/exotic-counts";
import {
  AbilityPointsDropdown,
  CorruptionDropdown,
  LevelDropdown,
  LoadoutDropdown,
  TechDropdown,
} from "../components/frame-toggle";
import { ProvidesValue } from "../components/provides-line";
import { StatCellView } from "../components/stat-cell";
import { StatesLegend } from "../components/states-legend";
import { UnitFacetChips } from "../components/unit-chips";
import {
  AbilityPointsSlotsNotice,
  CompareParamNotice,
  CorruptionSlotsNotice,
  LevelSlotsNotice,
  LoadoutSettledNotice,
  LoadoutSlotsNotice,
  TechSlotsNotice,
  UnhonouredLeverNotice,
  UrlNotice,
  UrlToken,
} from "../components/url-notice";
import {
  addAgainRefusal,
  addColumnAgain,
  agreedFrame,
  applyBulkAxis,
  bestValue,
  COMPARE_LIMIT,
  type CompareColumn,
  clampCompare,
  columnKey,
  compareOverflow,
  type FrameAxis,
  mergedColumns,
  parseCompareColumns,
  removeColumn,
  serializeCompareParam,
  setColumnFrame,
  surplusFrameSlots,
} from "../lib/compare";
import { dataset, unitsById } from "../lib/dataset";
import {
  craftCountText,
  disclosureById,
  SQUADRONS_NOTE,
  VIRTUAL_SUPPLY_NOTE,
} from "../lib/disclosures";
import { factionLinkSearch } from "../lib/faction-selection";
import { exactNumber } from "../lib/format";
import { parseFrames, type StatFrame, serializeColumns, serializeFrame } from "../lib/frame";
import { frameSquadrons } from "../lib/frame-fold";
import { type FrameTransition, reconcileFrame } from "../lib/frame-reconcile";
import {
  equipOnUnit,
  hasItemSlots,
  type LoadoutTransition,
  settleLoadout,
  unequipAllOnUnit,
  unequipOnUnit,
} from "../lib/loadout-index";
import {
  isLineGroup,
  lineRows,
  PROVIDES_NOTE,
  rowLabel,
  type StatGroupSpec,
  type StatRowSpec,
  sheetRows,
  sheetShowsHeading,
  statModelFor,
  type ViewKind,
  viewKindOf,
} from "../lib/stat-model";
import { useDocumentTitle } from "../lib/title";
import { unitLinkSearch } from "../lib/unit-link";
import { VIEWS } from "../lib/views";
import { leverSearch, useFactionLink } from "./lever-search";

// The comparison view (Issue 07, ADR 0002): /compare?units=<id>,<id>,… is the shareable
// matchup — the prototype's own URL form. Ids are the game's stable entity ids in column
// order; `tech` and `corruption` are the stat frame pair (lib/frame.ts, ADR 0007). Every
// cell comes straight from the dataset through the same frameCell join the roster and
// entity pages use — the comparison never recomputes a stat, so states and captions carry
// over verbatim, and under a tier every column reads the same tier block.
export const compareSearchSchema = z.object({
  units: z.string().optional().catch(undefined),
  // The Faction Selection, the stat frame's two keys, and the two per-hull levers — declared
  // once for this route and /unit (routes/lever-search.ts, ADR 0012). On this page every
  // frame key carries one `;` slot per column (ADR 0011); a column is one hull in one place,
  // exactly as an entity page is, so it may carry a Loadout and a Level of its own, and a
  // hull reached from a column starts bare of both (Issue 52).
  ...leverSearch,
});

const route = getRouteApi("/compare");

/** A column resolved against the dataset: the matchup's pair plus the unit it names. Ids the
 * dataset does not know have no column — /compare names those instead. `key` is the pair's,
 * so the same hull under two frames gives React two distinct keys, which is the whole point. */
interface ResolvedColumn {
  key: string;
  unit: Unit;
  frame: StatFrame;
  /** Which pair in the matchup this column is, so its own controls write its own slot and
   * leave every other column's alone (Issue 41). Not the screen position: an id the dataset
   * does not know keeps its pair and renders no column. */
  index: number;
  /** What settling this column's `?items=` slot completed, in the words the equivalent tick
   * would have used, or absent when the slot already named a Loadout a player could field. */
  settledNote?: string;
}

export type CompareSearch = z.infer<typeof compareSearchSchema>;

export interface CompareMatchup {
  /** The frame slots the URL named, in column order — longer than the matchup when a link
   * sets stats for columns it does not carry. */
  frames: StatFrame[];
  /** Every pair the link asked for, uncapped, so the view can count what it dropped. */
  requested: CompareColumn[];
  /** The matchup as rendered: the first COMPARE_LIMIT pairs, order untouched. */
  columns: CompareColumn[];
  overflow: number;
  surplus: number;
  /** Well-formed tokens the pair dedupe dropped: the same unit twice under the same stats. */
  merged: number;
}

/** The whole matchup from the URL (ADR 0011): the units param paired slot by slot with the
 * frame keys, deduplicated on the PAIR, capped at four pairs. Exported so the contract is
 * testable without rendering — this view's numbers are entirely a function of it. */
export function compareMatchup(search: CompareSearch): CompareMatchup {
  const frames = parseFrames(search);
  const requested = parseCompareColumns(search.units, frames);
  const columns = clampCompare(requested);
  return {
    frames,
    requested,
    columns,
    overflow: compareOverflow(requested),
    merged: mergedColumns(search.units, requested),
    surplus: surplusFrameSlots(
      frames,
      columns.map((column) => column.slot),
    ),
  };
}

/** The comparison's groups ARE the shared model's (ADR 0005 Game Order, declared once in
 * lib/stat-model.ts), for the union of the kinds in the set (ADR 0008): a starbase beside a
 * titan shows every row, two structures show the structures rows only. Exported so the
 * model's own test can prove this view never restates the order or the kind rule — the
 * drift this replaced was exactly a restatement that stopped matching. */
export function compareGroupsFor(units: readonly Pick<Unit, "kind">[]): readonly StatGroupSpec[] {
  const kinds = new Set<ViewKind>(units.map(viewKindOf));
  return statModelFor([...kinds]);
}

// The sticky label rail: the one column that stays put while unit columns scroll under
// it, so a phone user always knows which row they are reading. Opaque background and its
// own right border are what make the slide-under legible. Border colors are per-side
// (border-r-line, not border-line): the rows stack a faint top rule onto these cells, and
// the all-sides color utilities would collide, letting the later class recolor every side.
const RAIL_CELL =
  "sticky left-0 z-10 w-[150px] min-w-[150px] bg-surface border-r border-r-line px-4 text-left";
const DATA_CELL = "min-w-[190px] border-l border-l-line px-4 text-right align-baseline";

/** A row's sticky label cell. Both row kinds render exactly this — the only difference is
 * where the tooltip comes from (a derived column's formula for stat rows, the caller's own
 * sentence for fact rows), and the helper-underline treatment must follow whichever is
 * present. Shared so the rail can never drift between the two. */
function RowHeader({ label, title }: { label: string; title?: string }) {
  return (
    <th
      scope="row"
      className={clsx(
        RAIL_CELL,
        "border-t border-t-line-faint py-2 font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-ink-3",
        title && "cursor-help underline decoration-muted decoration-dotted underline-offset-2",
      )}
      title={title}
    >
      {label}
    </th>
  );
}

/** A muted aligned-cell answer for facts a unit simply does not have (no exotics, no
 * hangar, no virtual supply) — never a blank cell in an aligned row. */
function NoneCell({ title }: { title: string }) {
  return (
    <span
      className="cursor-help text-[13px] text-muted-2 underline decoration-muted decoration-dotted underline-offset-2"
      title={title}
    >
      none
    </span>
  );
}

/** A group's section-header row — the first row of its own tbody, so scope="rowgroup" is
 * real table semantics (the header labels the rows that follow in the group). */
function GroupRow({ title, span }: { title: string; span: number }) {
  return (
    <tr>
      <th
        scope="rowgroup"
        className={clsx(
          RAIL_CELL,
          "border-t border-t-line pt-3 pb-1 font-condensed text-[10.5px] font-semibold uppercase tracking-[0.18em] text-muted",
        )}
      >
        {title}
      </th>
      {Array.from({ length: span }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: purely presentational spacer cells
        <td key={i} className="border-t border-t-line" />
      ))}
    </tr>
  );
}

function CompareStatRow({ spec, columns }: { spec: StatRowSpec; columns: ResolvedColumn[] }) {
  // Each cell is read under ITS OWN column's frame (ADR 0011). The fold is memoised per
  // (unit, frame), so two columns of the same hull under two frames are two folds, and two
  // columns under one frame share one.
  const cells = columns.map((column) => spec.cell(column.unit, column.frame));
  const best = spec.best === undefined ? undefined : bestValue(cells, spec.best);
  const disclosure = disclosureById.get(spec.id);
  return (
    <tr>
      {/* The game's own per-unit label where every column agrees on it (two military
          structures read "Military Orbit Slot Cost"); the row's one name otherwise. */}
      <RowHeader
        label={rowLabel(
          spec,
          columns.map((column) => column.unit),
        )}
        title={disclosure?.formula}
      />
      {cells.map((cell, index) => {
        const column = columns[index];
        if (!column) return null;
        const isBest = best !== undefined && cell.state === "computed" && cell.value === best;
        return (
          <td key={column.key} className={clsx(DATA_CELL, "border-t border-t-line-faint py-2")}>
            <span className={clsx("font-mono text-[14px]", isBest && "font-semibold text-best")}>
              <StatCellView cell={cell} format={spec.format} />
              {isBest && <span className="sr-only"> (best value in this comparison)</span>}
            </span>
          </td>
        );
      })}
    </tr>
  );
}

/** A custom aligned row for facts that are not stat cells (exotics, squadrons, virtual
 * supply) — same rail and cell chrome, caller-provided content. */
function FactRow({
  label,
  title,
  columns,
  render,
}: {
  label: string;
  title?: string;
  columns: ResolvedColumn[];
  /** The column's own frame comes with its unit: a comparison column carries its own stat
   * frame (ADR 0011), and a fact can depend on it — a Provides line on the Loadout, a
   * hangar's craft counts on the same (Issue 48). */
  render: (unit: Unit, frame: StatFrame) => ReactNode;
}) {
  return (
    <tr>
      <RowHeader label={label} title={title} />
      {columns.map((column) => (
        <td key={column.key} className={clsx(DATA_CELL, "border-t border-t-line-faint py-2")}>
          {render(column.unit, column.frame)}
        </td>
      ))}
    </tr>
  );
}

/** The facts this view states that are not stat cells. Each returns null when no unit in the
 * comparison has the fact, so an all-or-nothing row never renders as a wall of "none". */
function ExoticsRow({ columns }: { columns: ResolvedColumn[] }) {
  return (
    <FactRow
      label="Exotics"
      title="Exotic components required to build the unit. Hover a cell for their names."
      columns={columns}
      render={(unit) =>
        unit.exoticPrice.length === 0 ? (
          <NoneCell title="This unit costs no exotic components." />
        ) : (
          <ExoticCounts unit={unit} />
        )
      }
    />
  );
}

function VirtualSupplyRow({ columns }: { columns: ResolvedColumn[] }) {
  if (!columns.some((column) => column.unit.virtualSupplyCost !== undefined)) return null;
  return (
    <FactRow
      label="Virtual supply"
      title={VIRTUAL_SUPPLY_NOTE}
      columns={columns}
      render={(unit) =>
        unit.virtualSupplyCost === undefined ? (
          <NoneCell title="The game files carry no virtual supply for this unit." />
        ) : (
          <span className="font-mono text-[14px]">{exactNumber(unit.virtualSupplyCost)}</span>
        )
      }
    />
  );
}

/** The squadrons a carrier fields, as a row INSIDE the Combat group since 2026-09-07 (user:
 * "Strikecraft feels like it can be on the Combat card") — it was a section of its own after
 * Efficiency. What a carrier fields is part of what it brings to a fight, and the entity
 * page fuses it into the Combat card the same way, so the two sheets state one thing once.
 * No heading of its own here for the same reason: "Squadrons" names the row, and a group
 * heading row inside a group would claim a group the model does not declare. */
function StrikecraftRow({ columns }: { columns: ResolvedColumn[] }) {
  if (!columns.some((column) => column.unit.squadrons !== undefined)) return null;
  return (
    <FactRow
      label="Squadrons"
      title={SQUADRONS_NOTE}
      columns={columns}
      render={(unit, columnFrame) => {
        // Each column carries its own frame (ADR 0011), and the hangar follows it since
        // Issue 48 — a hangar Ship Item grows the shared capacity.
        const squadrons = frameSquadrons(unit, columnFrame);
        return squadrons ? (
          <span className="flex flex-col items-end gap-0.5">
            <span className="font-mono text-[14px]">{squadrons.capacity}</span>
            {squadrons.capacity > 0 && (
              <span className="max-w-[24ch] text-right text-[11px] text-muted-2">
                {squadrons.options
                  .map((option) => craftCountText(option, option.name))
                  .join(" or ")}
              </span>
            )}
          </span>
        ) : (
          <NoneCell title="This unit fields no strikecraft squadrons." />
        );
      }}
    />
  );
}

// What this view adds to the shared model, keyed by the group it attaches to: rows appended
// INSIDE that group's rail, after every row the model declares. Keyed rather than inlined so
// an addition can never reorder the model's own rows — the failure this whole refactor undoes.
// There is no longer a second, section-shaped table beside this one: Strikecraft was the only
// entry in it and became a Combat row on 2026-09-07, so every addition this view makes now
// lands inside a group the model names.
export const APPENDED_ROWS: Record<string, (columns: ResolvedColumn[]) => ReactNode> = {
  // Both of these used to attach to a group of their own — the exotics to Price, the virtual
  // supply to Fleet — and the two groups merged into Cost on 2026-09-07. An append lands
  // after every row the model declares, so the pair is ordered here instead: the virtual
  // supply next, being the supply-family row, and the exotics last, matching the entity
  // page's card, where the exotics block closes with its fabricated-price total.
  Cost: (columns) => (
    <>
      <VirtualSupplyRow columns={columns} />
      <ExoticsRow columns={columns} />
    </>
  ),
  Combat: (columns) => <StrikecraftRow columns={columns} />,
};

function UnitColumnHeader({
  column,
  position,
  compareIds,
  addAgainTitle,
  onFrameChange,
  onAddAgain,
  onRemove,
}: {
  column: ResolvedColumn;
  /** Which column on screen this is, 1-based. The same hull may stand twice (ADR 0011), so
   * a control's accessible name needs more than the unit's name to say which one it moves. */
  position: number;
  compareIds: string[];
  /** Why a second copy of this hull cannot be drawn, or undefined when it can. */
  addAgainTitle?: string;
  onFrameChange: (next: StatFrame) => void;
  onAddAgain: () => void;
  onRemove: () => void;
}) {
  const factionLink = useFactionLink();
  const { unit, frame } = column;
  // This column's own transient state (ADR 0002: a panel belongs to the visit, not the link).
  // The header is keyed by its SLOT rather than by the column's pair key, which is a function
  // of the very frame these controls change — keyed by the pair, every tick remounted the
  // header and shut the panel the reader was ticking in.
  const [note, setNote] = useState<string>();
  // The Loadout's half alone — all the Ship Items panel may repeat as its own (the entity
  // page's rule; lib/frame-reconcile.ts, FrameTransition).
  const [itemsPanelNote, setItemsPanelNote] = useState<string>();
  const [itemsOpen, setItemsOpen] = useState(false);
  // A Ship Items action speaks for the Loadout and nothing else, so both lines say its note.
  const apply = (change: LoadoutTransition) => {
    setNote(change.note);
    setItemsPanelNote(change.note);
    onFrameChange(change.frame);
  };
  const applyFrame = (change: FrameTransition) => {
    setNote(change.note);
    setItemsPanelNote(change.loadoutNote);
    onFrameChange(change.frame);
  };
  // EVERY control in this column writes its frame through here, exactly as the entity page's
  // do: unticking research a ship item depends on takes that item off and names it, and a
  // hull whose slot count fell is refitted rather than left in a state the game cannot make.
  const changeFrame = (next: StatFrame) => applyFrame(reconcileFrame(unit, frame, next));
  return (
    <th scope="col" className={clsx(DATA_CELL, "w-[240px] pt-4 pb-3 text-left font-normal")}>
      <div className="flex flex-col items-start gap-2">
        {unit.art.tooltipPicture ? (
          <ArtImage
            asset={unit.art.tooltipPicture}
            alt=""
            className="h-auto w-full rounded border border-line-faint"
          />
        ) : (
          // aria-hidden like the picture's empty alt: without it this sentence would
          // become part of the column header's accessible name.
          <div
            aria-hidden
            className="flex aspect-[459/216] w-full items-center justify-center rounded border border-line-faint bg-surface-deep px-3 text-center text-[11px] text-muted-2"
          >
            The game files ship no tooltip picture for this unit.
          </div>
        )}
        <Link
          to="/unit/$unitId"
          params={{ unitId: unit.id }}
          // Frame and the compare set follow the link, like every unit-level link.
          search={unitLinkSearch(frame, factionLink, compareIds)}
          className="text-[14.5px] font-semibold text-ink underline decoration-line-strong underline-offset-2 hover:text-link hover:decoration-link"
          title={`Open the ${unit.name} page`}
        >
          {unit.name}
        </Link>
        <UnitFacetChips unit={unit} />
        {/* This column's OWN controls (ADR 0011, Issue 41; Issue 52 for the last two). The
            Tech panel offers only this unit's Race — one section, not every Race in the
            matchup — and the Corruption axis stays its own control beside it (ADR 0007), as a
            dropdown rather than four segments because four columns of segments do not hold at
            375 px. The three per-hull levers (ADR 0012, ADR 0015) sit below them, each only
            where this hull has the thing: no Item Slots is no Items control, no ladder is
            neither a Level nor an Ability Points control, which is what keeps four columns of
            five controls legible. */}
        <div className="flex flex-col items-start gap-1.5">
          <TechDropdown
            frame={frame}
            races={[unit.race]}
            label={`Tech for ${unit.name}, column ${position}`}
            onChange={changeFrame}
          />
          <CorruptionDropdown
            frame={frame}
            label={`Corruption for ${unit.name}, column ${position}`}
            onChange={changeFrame}
          />
          {hasItemSlots(unit) && (
            <LoadoutDropdown
              unit={unit}
              frame={frame}
              {...(itemsPanelNote === undefined ? {} : { note: itemsPanelNote })}
              label={`Ship items for ${unit.name}, column ${position}`}
              open={itemsOpen}
              onOpenChange={setItemsOpen}
              onEquip={(id) => apply(equipOnUnit(unit, frame, id))}
              onUnequip={(id) => apply(unequipOnUnit(unit, frame, id))}
              onClearAll={() => apply(unequipAllOnUnit(frame))}
            />
          )}
          {unit.leveled && (
            <LevelDropdown
              frame={frame}
              label={`Level for ${unit.name}, column ${position}`}
              onChange={changeFrame}
            />
          )}
          {/* The third per-hull lever (ADR 0015, Issue 63), on the same 33 hulls the Level
              control is on: the points a hull has are a function of its Level, so a column
              holding a hull with no ladder has no pool to spend and shows no control. */}
          {unit.leveled && (
            <AbilityPointsDropdown
              unit={unit}
              frame={frame}
              label={`Ability Points for ${unit.name}, column ${position}`}
              onChange={changeFrame}
            />
          )}
        </div>
        {/* What the last change to this column's Loadout or allocation did — the Tech control
            taking off an item this hull's slots no longer buy, the Level control trimming the
            Ability Levels it no longer permits (reconcileFrame says both). It sits under the
            controls while the Items panel is SHUT; the open panel says the Loadout's half in
            its own header, so exactly one of the two speaks (the entity page's rule). */}
        {note !== undefined && !itemsOpen && (
          <span className="text-[11.5px] leading-snug text-muted-2">{note}</span>
        )}
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <button
            type="button"
            aria-label={`Add another ${unit.name} column, from column ${position}`}
            title={
              addAgainTitle ??
              "Add a second column of this unit, so you can set the two to different stats"
            }
            disabled={addAgainTitle !== undefined}
            onClick={onAddAgain}
            className="cursor-pointer text-[11.5px] text-muted hover:text-link disabled:cursor-not-allowed disabled:text-muted-2 disabled:hover:text-muted-2"
          >
            Add again
          </button>
          <button
            type="button"
            aria-label={`Remove ${unit.name} from comparison, column ${position}`}
            onClick={onRemove}
            className="cursor-pointer text-[11.5px] text-muted hover:text-vasari"
          >
            Remove
          </button>
        </span>
      </div>
    </th>
  );
}

export function ComparePage() {
  useDocumentTitle("Comparison");
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const { frames, requested, columns: pairs, overflow, surplus, merged } = compareMatchup(search);
  // Each column carries its own frame (ADR 0011), so the page-level controls are the BULK
  // ones: they set one axis of EVERY column in one action, which is still the common case.
  // They edit from the first column's frame and say "mixed" on an axis the columns disagree
  // on, rather than claiming one column's stats for all of them. With no column yet, they
  // show the link's own slot.
  // What a BULK change did to the columns' Loadouts, said out loud (Issue 52). Transient like
  // the per-column note beside it: it describes an action, not a link.
  const [bulkNote, setBulkNote] = useState<string>();
  // Each slot is settled on its own, the way each column carries its own frame (ADR 0011):
  // a `?items=` slot has never been through a tick, so the rules that make a Loadout one the
  // game could field have never run on it (issue 45's cold-parse gap, found reviewing the
  // Cloak round 2026-09-07). Settled BEFORE the bulk controls read the frames, because those
  // controls edit from what the columns show — reading the link's raw slots there would have
  // them say "base" over a column the settle had already given research to, and using one
  // would then write that "base" back over every column.
  const settled = pairs.map((pair) => {
    const unit = unitsById.get(pair.id);
    return unit === undefined ? { frame: pair.frame } : settleLoadout(unit, pair.frame);
  });
  const bulk = agreedFrame(settled.map((one) => one.frame));
  const frame = bulk.frame;
  const columns: ResolvedColumn[] = pairs.flatMap((pair, index) => {
    const unit = unitsById.get(pair.id);
    const one = settled[index];
    if (unit === undefined || one === undefined) return [];
    // The key stays the PAIR's, not the settled frame's — it is deterministic from the pair
    // either way, and keying on the frame the controls change remounts the header the reader
    // is ticking in.
    return [
      {
        key: columnKey(pair),
        unit,
        frame: one.frame,
        index,
        ...(one.note === undefined ? {} : { settledNote: one.note }),
      },
    ];
  });
  const units = columns.map((column) => column.unit);
  // The selection rides this page's links but narrows nothing on it (ADR 0010).
  const compareIds = [...new Set(pairs.map((pair) => pair.id))];
  const unknownIds = [...new Set(pairs.filter((pair) => !unitsById.has(pair.id)).map((p) => p.id))];

  /** The matchup back into the URL: the ids in column order beside their own frame slots.
   * All FOUR keys (Issue 52), because a column carries the two per-hull levers as well —
   * serializeColumns is the comparison's own writer, and the narrow serializeFrame stays the
   * thing links are written with. */
  function writeColumns(next: readonly CompareColumn[]) {
    void navigate({
      search: (prev) => ({
        ...prev,
        units: serializeCompareParam(next.map((column) => column.id)),
        ...serializeColumns(next.map((column) => column.frame)),
      }),
    });
  }

  /** A bulk control: one click sets ITS axis on every column, leaving each column's other
   * axis alone (bulkAxis). The control names the axis rather than having it inferred from
   * what changed, because a matchup whose columns disagree has no value to infer from —
   * "Deselect all" on a Base-beside-Fully-Teched matchup must reach the second column even
   * though the panel it was clicked in was showing the first column's Base. An empty
   * comparison has no column to carry the frame, so it writes one slot — which becomes the
   * first column's the moment a unit arrives. */
  function onBulkChange(next: StatFrame, axis: FrameAxis) {
    if (pairs.length === 0) {
      void navigate({ search: (prev) => ({ ...prev, ...serializeColumns([next]) }) });
      return;
    }
    // Each column keeps its own Loadout and Level — the bulk controls name the research or the
    // Corruption axis and touch nothing else — and every column is refitted against its OWN
    // hull (applyBulkAxis). The move starts from the SETTLED frames, one per pair: what the
    // columns show, not what the link said.
    const moved = applyBulkAxis(
      settled.map((one, index) => ({
        frame: one.frame,
        unit: unitsById.get(pairs[index]?.id ?? ""),
      })),
      next,
      axis,
    );
    setBulkNote(moved.notes.length === 0 ? undefined : moved.notes.join(" "));
    void navigate({ search: (prev) => ({ ...prev, ...serializeColumns(moved.frames) }) });
  }

  /** One column's own control writing its own slot, every other column untouched. */
  function onColumnFrame(index: number, next: StatFrame) {
    writeColumns(setColumnFrame(pairs, index, next));
  }

  /** A second column of a hull that already stands (ADR 0011: the same hull may stand up to
   * four times). The copy starts from the bulk frame, or from the other end of the research
   * axis when that column already stands — lib/compare owns the rule. */
  function onAddAgain(id: string) {
    const next = addColumnAgain(pairs, id, frame);
    if (next !== undefined) writeColumns(next);
  }

  function onRemoveColumn(key: string) {
    writeColumns(removeColumn(pairs, key));
    // The clicked button unmounts with its column; without a new home focus falls to
    // <body> and a keyboard user loses their place. The next Remove (or the main
    // landmark when the last column went) is the nearest equivalent.
    requestAnimationFrame(() => {
      const next = document.querySelector<HTMLButtonElement>("th button[aria-label^='Remove']");
      if (next) next.focus();
      else document.querySelector<HTMLElement>("main")?.focus();
    });
  }

  // No page-level line claims stats for the matchup (ADR 0011): each column declares its own
  // in its own header, because with per-column frames a single label could only ever be right
  // about one column. What is left is the column count — the best-per-row rule went with the
  // rest of the captions (user decision 2026-09-06); the marks are visible in the table and
  // the states legend beside this line is where the glyphs are explained.
  const note = `${units.length} ${units.length === 1 ? "unit" : "units"}`;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-y-2">
        <h1 className="text-[22px] font-semibold tracking-tight">Comparison</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* The bulk controls, labelled for what they do: one click each, every column. */}
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
            All columns
          </span>
          <TechDropdown
            frame={frame}
            races={[...new Set(units.map((unit) => unit.race))]}
            mixed={bulk.techMixed}
            label="Tech for every column"
            onChange={(next) => onBulkChange(next, "research")}
          />
          <CorruptionDropdown
            frame={frame}
            mixed={bulk.corruptionMixed}
            label="Corruption for every column"
            onChange={(next) => onBulkChange(next, "corruption")}
          />
          {units.length > 0 && <span className="font-mono text-[12.5px] text-muted-2">{note}</span>}
          <StatesLegend />
        </div>
      </div>

      {/* The cap gets its own wording here rather than the shared one, so `reportOverflow`
          stays off: "keeps", not "shows" — ids inside the cap can still be unknown to this
          dataset, so the kept count and the rendered column count can differ. */}
      <CompareParamNotice value={search.units} requested={requested} />
      {overflow > 0 && (
        <UrlNotice>
          This link lists {requested.length} columns; the comparison keeps the first {COMPARE_LIMIT}
          .
        </UrlNotice>
      )}
      {unknownIds.length > 0 && (
        <UrlNotice>
          Not in this dataset (game {dataset.meta.gameVersion}):{" "}
          <UrlToken>{unknownIds.join(", ")}</UrlToken>. A unit id can change or leave the game with
          a patch.
        </UrlNotice>
      )}
      {merged > 0 && (
        <UrlNotice>
          {merged === 1 ? "One column is" : `${merged} columns are`} not drawn: this link names a
          unit under stats it already stands under, and two identical columns would print the same
          numbers twice. Set a column's Tech, Corruption, Level or ship items — or use Add again —
          to hold one unit under two.
        </UrlNotice>
      )}
      {surplus > 0 && (
        <UrlNotice>
          This link sets stats for {frames.length} columns; the comparison has {pairs.length}. The{" "}
          {surplus === 1 ? "extra setting changes" : "extra settings change"} nothing here.
        </UrlNotice>
      )}
      <CorruptionSlotsNotice value={search.corruption} />
      <TechSlotsNotice value={search.tech} />
      <LoadoutSlotsNotice value={search.items} />
      <LevelSlotsNotice value={search.level} />
      <AbilityPointsSlotsNotice value={search.points} />
      <UnhonouredLeverNotice columns={columns} />
      <LoadoutSettledNotice
        notes={columns.flatMap((column) =>
          column.settledNote === undefined ? [] : [column.settledNote],
        )}
      />
      {/* What a bulk change did to the columns' Loadouts — a per-column change says so in its
          own column instead. */}
      {bulkNote !== undefined && <p className="text-[13px] text-muted-2">{bulkNote}</p>}

      {units.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-line-strong px-6 py-12 text-center text-[13.5px] text-ink-3">
          <span>Nothing selected yet. Tick units on Ships or Structures to line them up here.</span>
          <span className="flex flex-wrap justify-center gap-4">
            {VIEWS.map((view) => (
              <Link
                key={view.path}
                to={view.path}
                search={{ ...serializeFrame(frame), ...factionLinkSearch(search) }}
                className="text-[14px] text-link hover:text-link-hover"
              >
                Open {view.navLabel}
              </Link>
            ))}
          </span>
        </div>
      ) : (
        // `relative` is load-bearing: the sr-only best-value spans are position:absolute,
        // and without a positioned ancestor inside this scroller their boxes anchor to
        // the document and widen the PAGE to the table's full width at phone widths
        // (caught live at 390px in the review round). Positioning the scroller makes it
        // their containing block, so they clip and scroll with the table.
        <div className="relative overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full border-separate border-spacing-0 text-[14px] [font-variant-numeric:tabular-nums]">
            <thead>
              <tr>
                <td className={clsx(RAIL_CELL, "align-bottom")} />
                {columns.map((column, position) => (
                  <UnitColumnHeader
                    // Keyed by the column's SLOT, which its own controls never change: the
                    // pair key moves with every tick, and a remounted header loses the open
                    // Items panel and the sentence naming what the last change did.
                    key={column.index}
                    column={column}
                    position={position + 1}
                    compareIds={compareIds}
                    addAgainTitle={addAgainRefusal(pairs, column.unit.id, frame)}
                    onFrameChange={(next) => onColumnFrame(column.index, next)}
                    onAddAgain={() => onAddAgain(column.unit.id)}
                    onRemove={() => onRemoveColumn(column.key)}
                  />
                ))}
              </tr>
            </thead>
            {/* One tbody per model group: the GroupRow's scope="rowgroup" then labels
                exactly the rows that follow it, which is the table semantics the section
                visually claims. Where the model hides that heading — a one-row group whose
                row already carries the group's word — the tbody carries the name itself, so
                the row group is never anonymous just because its heading would have read the
                same word twice (found in review, 2026-09-07). Order, labels, cells and which
                rows the set's kinds get are the model's — this view only decides how a row
                is drawn. */}
            {compareGroupsFor(units).map((group) => {
              // A list-valued group (Provides): one row per label present in the set, a
              // unit without that line an empty cell (a fact list, never a glyph). With
              // no label in the set the group renders nothing, not a bare heading.
              const lines = lineRows(group, columns);
              if (isLineGroup(group) && lines.length === 0) {
                return null;
              }
              return (
                <tbody key={group.title} aria-label={group.title}>
                  {/* A one-row group whose row carries the group's own name prints that
                        word once, not twice — the model's rule, shared with the entity
                        page's cards (sheetShowsHeading, Issue 56). */}
                  {sheetShowsHeading(group) && (
                    <GroupRow title={group.title} span={columns.length} />
                  )}
                  {/* The model's rows for this group, then what the two stat SHEETS
                        append to it (Antimatter, Issue 50): sheetRows is the one
                        declaration this rail and the entity page's cards share. */}
                  {sheetRows(group).map((spec) => (
                    <CompareStatRow key={spec.id} spec={spec} columns={columns} />
                  ))}
                  {lines.map((row) => (
                    <FactRow
                      key={row.id}
                      label={row.label}
                      title={PROVIDES_NOTE}
                      columns={columns}
                      render={(unit, columnFrame) => (
                        <ProvidesValue line={row.lineOf(unit, columnFrame)} />
                      )}
                    />
                  ))}
                  {APPENDED_ROWS[group.title]?.(columns)}
                </tbody>
              );
            })}
          </table>
        </div>
      )}
    </section>
  );
}
